// Relationships: the routes, the sidebar rows and the slot items of
// `wealth.crm.relationships` (ADR-0027).
import { lazy, Suspense, useState } from 'react';
import type { TriTierClientRow } from '@septima/contract';
import { Button } from '@/components/ui';
import { defineWebFeature } from '@/features/kit';
import type { ClientBookTableCtx } from '@/components/client-book/tri-tier-slots';
import { LINK_STATUS_LABELS, needsClientLinks } from '@/components/client-book/client-links-plan';

const CompleteClientLinksDialog = lazy(
    () => import('@/components/client-book/CompleteClientLinksDialog'),
);

/**
 * The "Complete client links" action of one tri-tier row (SEP-300). It shows
 * only on a row with a missing or inconsistent link, and it names the gap
 * beside the button. The cell itself is not lazy: the table measures the width
 * of each column from its first render, and a lazy cell renders empty then.
 * Only the dialog loads on demand.
 */
function CompleteClientLinksCell({ row }: { row: TriTierClientRow }) {
    const [open, setOpen] = useState(false);
    if (!needsClientLinks(row.linkStatus)) return null;
    return (
        <div className="flex flex-col items-start gap-0.5">
            <span className="whitespace-nowrap text-annotation text-[rgb(var(--color-rag-amber))]">
                {LINK_STATUS_LABELS[row.linkStatus]}
            </span>
            <Button
                variant="secondary"
                size="xs"
                className="whitespace-nowrap"
                onClick={() => setOpen(true)}
            >
                Complete client links
            </Button>
            {open && (
                <Suspense fallback={null}>
                    <CompleteClientLinksDialog row={row} isOpen onClose={() => setOpen(false)} />
                </Suspense>
            )}
        </div>
    );
}

/** The order of the link status in the column sort: the largest gap first. */
const LINK_STATUS_ORDER = {
    no_suitability_record: 0,
    no_contact: 1,
    anchor_differs: 2,
    complete: 3,
} as const;

export default defineWebFeature({
    id: 'wealth.crm.relationships',
    routes: [
        {
            frontend: 'desk',
            path: 'relationships',
            lazy: () => import('@/pages/desk/relationships/page'),
        },
        {
            frontend: 'ops',
            path: 'relationships',
            lazy: () => import('@/pages/ops/relationships/page'),
        },
    ],
    slots: {
        // SEP-300: complete the links of one Client Book row from the row. Desk
        // and Ops change relationships, and Comply and View only read them, so
        // the column shows in the two views that can act. The server gate is
        // the gate of the relationship create, and it does not move.
        'client-book.tri-tier.columns': [
            {
                key: 'complete-client-links',
                order: 10,
                personas: ['desk', 'im-assistant', 'ops'],
                when: (ctx: ClientBookTableCtx) => ctx.view === 'desk' || ctx.view === 'ops',
                column: () => ({
                    id: 'links',
                    header: 'Links',
                    minWidth: '190px',
                    sortable: true,
                    sortValue: (r) => LINK_STATUS_ORDER[r.linkStatus],
                    // The export holds the text that the cell shows, not the rank.
                    exportValue: (r) =>
                        needsClientLinks(r.linkStatus) ? LINK_STATUS_LABELS[r.linkStatus] : null,
                    cell: (r) => <CompleteClientLinksCell row={r} />,
                }),
            },
        ],
    },
    nav: [
        // The book-level relationship register. It is a page of its own again,
        // under Septima CRM beside the three tiers it draws its endpoints from.
        // SEP-176 had folded it into the Desk Dashboard as a tab. The row is open
        // to an im-assistant, who reads the links of the book of the IM it acts
        // for (D0.3; Dowgate ruling of 2026-09-30). The declared section keeps
        // the route under the persona policy: a route no section owns is open
        // to every persona of the view.
        {
            frontend: 'desk',
            key: 'relationships',
            label: 'Relationships',
            href: '/desk/relationships',
            icon: 'Network',
            group: 'septima-crm',
            order: 240,
        },
        // The firm-wide relationship register. Ops exports it to map each contact
        // to its Account reference in Tercero.
        {
            frontend: 'ops',
            key: 'relationships',
            label: 'Relationships',
            href: '/ops/relationships',
            icon: 'Network',
            group: 'client-operations',
            order: 450,
        },
    ],
});
